// src/app/lib/notify.ts
//
// The one way this app tells someone an operation finished.
//
// Every save, delete, sync or long-running action should end in a notify.*
// call rather than an inline banner that the next render throws away. Use it
// for anything the user STARTED and cannot see the result of at a glance;
// don't use it for validation of a field they are still editing, which
// belongs next to the field.
//
//   notify.success("Connection saved");
//   notify.success("Connection saved", { description: "3 users updated" });
//   notify.error(e, { fallback: "Couldn't save the connection" });
//
// Why a wrapper rather than sonner directly:
//   * every notification gets a close button, so nothing is stuck on screen;
//   * errors stay up long enough to read and copy (sonner's default is too
//     short for a message someone has to act on);
//   * every notification is kept in a short history, so one dismissed by
//     accident can be brought back with notify.showLast(). That is the piece
//     a plain toast cannot do: once it fades, the text is gone.
//
// The <Toaster position="top-right" /> in App.tsx is what renders these;
// there is exactly one, mounted app-wide.

import { toast } from "sonner";

export type NotifyKind = "success" | "error" | "warning" | "info";

export interface NotifyOptions {
  /** Second line — the detail behind the headline. */
  description?: string;
  /** Milliseconds on screen. Defaults by kind; Infinity pins it open. */
  duration?: number;
  /** For notify.error when handed an unknown throwable. */
  fallback?: string;
}

export interface NotifyEntry {
  id: number;
  kind: NotifyKind;
  message: string;
  description?: string;
  at: number;
}

const DEFAULT_DURATION: Record<NotifyKind, number> = {
  success: 4000,
  info: 5000,
  warning: 7000,
  // Long enough to read a server message and copy an id out of it.
  error: 10000,
};

const HISTORY_LIMIT = 25;
const history: NotifyEntry[] = [];
let nextId = 1;

/** Whatever was thrown, as something worth showing a person. */
function messageFrom(error: unknown, fallback: string): string {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object") {
    const maybe = (error as { message?: unknown }).message;
    if (typeof maybe === "string" && maybe.trim()) return maybe;
  }
  return fallback;
}

function emit(entry: NotifyEntry, duration: number): void {
  const options = {
    id: `notify-${entry.id}`,
    description: entry.description,
    duration,
    closeButton: true,
  };
  if (entry.kind === "success") toast.success(entry.message, options);
  else if (entry.kind === "error") toast.error(entry.message, options);
  else if (entry.kind === "warning") toast.warning(entry.message, options);
  else toast.info(entry.message, options);
}

function push(kind: NotifyKind, message: string, opts: NotifyOptions = {}): NotifyEntry {
  const entry: NotifyEntry = {
    id: nextId++,
    kind,
    message,
    description: opts.description,
    at: Date.now(),
  };
  history.unshift(entry);
  history.length = Math.min(history.length, HISTORY_LIMIT);
  emit(entry, opts.duration ?? DEFAULT_DURATION[kind]);
  return entry;
}

export const notify = {
  /** An operation finished and did what was asked. */
  success: (message: string, opts?: NotifyOptions) => push("success", message, opts),

  /** An operation failed. Takes a message, or the thrown value itself. */
  error: (error: unknown, opts: NotifyOptions = {}) =>
    push("error", messageFrom(error, opts.fallback ?? "Something went wrong"), opts),

  /** It worked, but not the way they probably wanted. */
  warning: (message: string, opts?: NotifyOptions) => push("warning", message, opts),

  /** Neither — something happened that the user should know about. */
  info: (message: string, opts?: NotifyOptions) => push("info", message, opts),

  /** Show a past notification again. Defaults to the most recent one.
   *  Returns false when there is nothing to show. */
  showLast(id?: number): boolean {
    const entry = id === undefined ? history[0] : history.find((e) => e.id === id);
    if (!entry) return false;
    emit(entry, DEFAULT_DURATION[entry.kind]);
    return true;
  },

  /** Most recent first. A copy — callers can't reorder the real thing. */
  history: (): NotifyEntry[] => [...history],

  /** The most recent notification, or undefined. */
  last: (): NotifyEntry | undefined => history[0],

  /** Take notifications off the screen, keeping the history so showLast()
   *  still works. */
  dismiss: (id?: number) =>
    toast.dismiss(id === undefined ? undefined : `notify-${id}`),

  /** Off the screen AND out of the history. */
  clear(): void {
    history.length = 0;
    toast.dismiss();
  },
};

export default notify;
